import { Injectable, OnDestroy, inject } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Subscription, catchError, filter, of } from 'rxjs';
import { environment } from '../../../environments/environment';
import { ApiService } from './api.service';

export interface LmsActivityHeartbeatPayload {
  parcoursId?: string | null;
  durationSeconds?: number;
}

export interface LmsActivityHeartbeatResult {
  ok: boolean;
}

const HEARTBEAT_INTERVAL_MS = 60_000;
const IDLE_TIMEOUT_MS = 10 * 60_000;
const MAX_DURATION_SECONDS = 120;

/**
 * Periodic LMS activity heartbeat for authenticated learners.
 * - every 1 min while active
 * - on session close (logout / tab close)
 * - when idle reaches 10 min (flush then pause until activity)
 *
 * @route POST /api-v2/lms/activity/heartbeat
 */
@Injectable({ providedIn: 'root' })
export class LmsActivityHeartbeatService implements OnDestroy {
  private readonly api = inject(ApiService);
  private readonly router = inject(Router);

  private started = false;
  private paused = false;
  private intervalId: ReturnType<typeof setInterval> | null = null;
  private idleTimerId: ReturnType<typeof setTimeout> | null = null;
  private segmentStartedAt = 0;
  private parcoursId: string | null = null;
  private routerSub?: Subscription;

  private readonly onActivity = (): void => this.noteActivity();
  private readonly onVisibility = (): void => this.onVisibilityChange();
  private readonly onPageHide = (): void => this.flushOnSessionClose(true);

  start(): void {
    if (this.started || typeof window === 'undefined' || !this.hasActiveSession()) {
      return;
    }
    this.started = true;
    this.paused = false;
    this.parcoursId = this.extractParcoursId(this.router.url);
    this.segmentStartedAt = Date.now();
    this.bindListeners();
    this.startInterval();
    this.resetIdleTimer();

    this.routerSub = this.router.events
      .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
      .subscribe((event) => {
        if (!this.hasActiveSession()) {
          this.teardown();
          return;
        }
        this.parcoursId = this.extractParcoursId(event.urlAfterRedirects);
      });
  }

  stop(): void {
    if (!this.started) {
      return;
    }
    if (this.hasActiveSession()) {
      this.flushOnSessionClose(false);
    }
    this.teardown();
  }

  ngOnDestroy(): void {
    this.teardown();
  }

  /**
   * Flush pending duration (logout / tab close).
   * Call before clearing the auth token.
   */
  flushOnSessionClose(useKeepalive = false): void {
    if (!this.started || !this.hasSessionToken()) {
      return;
    }
    const durationSeconds = this.consumePendingDuration();
    this.clearInterval();
    this.clearIdleTimer();
    this.sendHeartbeat({ parcoursId: this.parcoursId, durationSeconds }, useKeepalive);
  }

  private bindListeners(): void {
    window.addEventListener('mousemove', this.onActivity, { passive: true });
    window.addEventListener('mousedown', this.onActivity, { passive: true });
    window.addEventListener('keydown', this.onActivity, { passive: true });
    window.addEventListener('scroll', this.onActivity, { passive: true });
    window.addEventListener('touchstart', this.onActivity, { passive: true });
    document.addEventListener('visibilitychange', this.onVisibility);
    window.addEventListener('pagehide', this.onPageHide);
    window.addEventListener('beforeunload', this.onPageHide);
  }

  private unbindListeners(): void {
    window.removeEventListener('mousemove', this.onActivity);
    window.removeEventListener('mousedown', this.onActivity);
    window.removeEventListener('keydown', this.onActivity);
    window.removeEventListener('scroll', this.onActivity);
    window.removeEventListener('touchstart', this.onActivity);
    document.removeEventListener('visibilitychange', this.onVisibility);
    window.removeEventListener('pagehide', this.onPageHide);
    window.removeEventListener('beforeunload', this.onPageHide);
  }

  private teardown(): void {
    this.started = false;
    this.paused = false;
    this.clearInterval();
    this.clearIdleTimer();
    this.unbindListeners();
    this.routerSub?.unsubscribe();
    this.routerSub = undefined;
    this.segmentStartedAt = 0;
  }

  private startInterval(): void {
    this.clearInterval();
    this.intervalId = setInterval(() => this.tick(), HEARTBEAT_INTERVAL_MS);
  }

  private clearInterval(): void {
    if (this.intervalId != null) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
  }

  private resetIdleTimer(): void {
    this.clearIdleTimer();
    this.idleTimerId = setTimeout(() => this.onIdleTimeout(), IDLE_TIMEOUT_MS);
  }

  private clearIdleTimer(): void {
    if (this.idleTimerId != null) {
      clearTimeout(this.idleTimerId);
      this.idleTimerId = null;
    }
  }

  private noteActivity(): void {
    if (!this.started || document.hidden || !this.hasActiveSession()) {
      return;
    }
    if (this.paused) {
      this.paused = false;
      this.segmentStartedAt = Date.now();
      this.startInterval();
    }
    this.resetIdleTimer();
  }

  private onVisibilityChange(): void {
    if (!this.started || !this.hasActiveSession()) {
      return;
    }
    if (document.hidden) {
      this.flushPending(false);
      this.clearInterval();
      this.clearIdleTimer();
      return;
    }
    this.paused = false;
    this.segmentStartedAt = Date.now();
    this.startInterval();
    this.resetIdleTimer();
  }

  private onIdleTimeout(): void {
    if (!this.started || this.paused || !this.hasActiveSession()) {
      return;
    }
    this.flushPending(false);
    this.paused = true;
    this.clearInterval();
  }

  private tick(): void {
    if (!this.started || this.paused || document.hidden || !this.hasActiveSession()) {
      return;
    }
    this.flushPending(false);
  }

  private flushPending(useKeepalive: boolean): void {
    if (!this.hasActiveSession()) {
      return;
    }
    const durationSeconds = this.consumePendingDuration();
    if (durationSeconds <= 0) {
      this.segmentStartedAt = Date.now();
      return;
    }
    this.sendHeartbeat({ parcoursId: this.parcoursId, durationSeconds }, useKeepalive);
  }

  private consumePendingDuration(): number {
    if (!this.segmentStartedAt) {
      return 0;
    }
    const elapsedSeconds = Math.floor((Date.now() - this.segmentStartedAt) / 1000);
    this.segmentStartedAt = Date.now();
    return Math.max(0, Math.min(MAX_DURATION_SECONDS, elapsedSeconds));
  }

  private sendHeartbeat(payload: LmsActivityHeartbeatPayload, useKeepalive: boolean): void {
    if (!this.hasSessionToken() || this.isAuthRoute(this.router.url)) {
      return;
    }

    const body: LmsActivityHeartbeatPayload = {
      parcoursId: payload.parcoursId || null,
      durationSeconds: Math.max(0, Math.min(MAX_DURATION_SECONDS, payload.durationSeconds ?? 0))
    };

    if (useKeepalive) {
      this.sendKeepalive(body);
      return;
    }

    this.api
      .post<LmsActivityHeartbeatResult>('lms/activity/heartbeat', body)
      .pipe(catchError(() => of({ ok: false } as LmsActivityHeartbeatResult)))
      .subscribe();
  }

  private sendKeepalive(payload: LmsActivityHeartbeatPayload): void {
    const token = localStorage.getItem('token')?.trim();
    if (!token || this.isAuthRoute(this.router.url)) {
      return;
    }
    const baseUrl = environment.apiUrl.replace(/\/+$/, '');
    try {
      void fetch(`${baseUrl}/lms/activity/heartbeat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          token
        },
        body: JSON.stringify(payload),
        keepalive: true,
        credentials: 'same-origin'
      });
    } catch {
      /* ignore unload failures */
    }
  }

  private hasActiveSession(): boolean {
    return this.hasSessionToken() && !this.isAuthRoute(this.router.url);
  }

  private hasSessionToken(): boolean {
    return !!localStorage.getItem('token')?.trim();
  }

  private isAuthRoute(url: string): boolean {
    const path = (url.split('?')[0] || '').toLowerCase();
    return (
      path.includes('/connexion') ||
      path.includes('/login') ||
      path.includes('/connecting') ||
      path.includes('/reset-password') ||
      path.includes('/change-password') ||
      path.includes('/mot-de-passe')
    );
  }

  private extractParcoursId(url: string): string | null {
    const match = url.match(/\/etudiant\/ma-formation\/([^/?#]+)/);
    const id = match?.[1]?.trim();
    if (!id || id === 'lecture') {
      return null;
    }
    return id;
  }
}
